import { Helper } from '@/mixin/helper.js'
import WsVoip from './ws.js'
import { float32ToInt16PCM } from './utils.js'
import { alawFromPCM } from './g711a.js'
import { playG711Audio } from './play-audio.js'

export default class Voip {
  constructor({ token, vehicleId, audioContext, bufferSize, noiseConcellationConfig }) {
    this.audioContext = audioContext
    this.sourceNode = null
    this.scriptProcessorNode = null
    this.mediaStream = null
    this.socket = null
    this.token = token
    this.recording = []
    this.recorder = null
    this.wavWorker = null
    this.g711Processor = null
    this.bufferSize = Number(bufferSize || 640)
    this.micThreshold = {
      rms: Number(noiseConcellationConfig.thresholdRms || 0.05),
      stdDev: Number(noiseConcellationConfig.thresholdStdDev || 0.03),
      amplitude: Number(noiseConcellationConfig.thresholdAmplitude || 0.2),
    }
    this.isDeviceMuted = false
    this.lastMessageTimestamp = Date.now()
    this.messageTimeout = 3000
    this.checkMessageInterval = null
    this.isMessageHaveBeenReceived = false
    this.isDeviceBuffering = false
    this.filter = null
    this.gain = null
    this.onIncomingAudioLevel = null // Callback for incoming audio level (from server)

    // setup noise cancellation
    let calculatedHasNoiseCancellation = false
    if (
      (noiseConcellationConfig.hasScope &&
        noiseConcellationConfig.isVehicleFilterNoiseConcellationActive &&
        noiseConcellationConfig.listVehicleIdNoiseCancellation.includes(vehicleId)) ||
      (noiseConcellationConfig.hasScope &&
        !noiseConcellationConfig.isVehicleFilterNoiseConcellationActive)
    ) {
      calculatedHasNoiseCancellation = true
    }
    this.calculatedHasNoiseCancellation = calculatedHasNoiseCancellation

    // ws url
    this.wsDomain = calculatedHasNoiseCancellation
      ? process.env.VUE_APP_VOIP_WS_URL_SERVER_INDO
      : process.env.VUE_APP_VOIP_WS_URL
  }
  // Function to start capturing audio
  async startCapture({
    callbackState: {
      callbackCallActiveAfterBuffering,
      callbackDeviceBuffering,
      callbackCallsOnGoing,
      callbackCallsFailed,
      callbackWsError,
    },
  }) {
    // eslint-disable-next-line no-useless-catch
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
        },
      })

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
      this.filter = this.audioContext.createBiquadFilter()
      this.gain = this.audioContext.createGain()

      this.socket = new WsVoip()

      await this.socket.connect(
        `${this.wsDomain}/ws?token=` + this.token,
        this.addEventListenerWebSocket(
          callbackCallActiveAfterBuffering,
          callbackDeviceBuffering,
          callbackCallsOnGoing,
          callbackCallsFailed,
          callbackWsError
        )
      )

      // Set up incoming audio level callback
      // Store reference so we can clean it up later
      this.audioLevelHandler = (event) => {
        if (event.data.type === 'audioLevel' && this.onIncomingAudioLevel) {
          this.onIncomingAudioLevel(event.data.audioLevel)
        }
      }
      this.socket.workletNode.port.onmessage = this.audioLevelHandler

      // Load both worklets
      await this.audioContext.audioWorklet.addModule('/voip/recorder.worklet.js')
      await this.audioContext.audioWorklet.addModule('/voip/g711-processor.js')

      this.recorder = new AudioWorkletNode(this.audioContext, 'recorder.worklet')
      this.g711Processor = new AudioWorkletNode(this.audioContext, 'g711-processor')

      // Anti-aliasing low-pass filter before downsampling to 8kHz
      // Cutoff at 3.8kHz to prevent aliasing (below Nyquist frequency of 4kHz)
      this.filter.type = 'lowpass'
      this.filter.frequency.value = 3800
      this.filter.Q.value = 0.707 // Butterworth response

      // Gain control
      this.gain.gain.value = 3

      // Connect the audio chain:
      // sourceNode -> gain -> anti-aliasing filter -> recorder (downsamples to 8kHz)
      this.sourceNode.connect(this.gain)
      this.gain.connect(this.filter)
      this.filter.connect(this.recorder)

      // For monitoring only (optional, can be removed in production)
      this.recorder.connect(this.g711Processor)
      this.g711Processor.connect(this.audioContext.destination)
      this.recorder.connect(this.audioContext.destination)

      // Get the actual sample rate from the audio context
      const sourceSampleRate = this.audioContext.sampleRate
      const targetSampleRate = 8000

      this.recorder.port.postMessage({
        bufferSize: this.bufferSize,
        threshold: JSON.stringify(this.micThreshold),
        hasNoiseCancellation: this.calculatedHasNoiseCancellation,
        sourceSampleRate: sourceSampleRate,
        targetSampleRate: targetSampleRate,
      })

      this.recorder.port.onmessage = (e) => {
        let x = float32ToInt16PCM(e.data)
        const newdata = alawFromPCM(x)
        this.socket.send(newdata)
      }

      await Helper.methods.delay(2000)
      return true
    } catch (err) {
      console.log(err)
      callbackCallsFailed()
    }
  }

  // Function to stop capturing audio
  stopCapture() {
    this.recording = []

    // Clean up audio level listener to prevent memory leak
    if (this.socket && this.socket.workletNode && this.audioLevelHandler) {
      this.socket.workletNode.port.onmessage = null
      this.audioLevelHandler = null
    }

    // Clear callback reference
    this.onIncomingAudioLevel = null

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop()
        }
      })
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
    }
    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect()
    }
    if (this.socket) {
      this.socket.close()
    }
    if (this.recorder) {
      this.recorder.disconnect()
    }
  }
  // Then add a method to control mute/unmute
  toggleMuteDevice(isMuted) {
    if (isMuted) {
      this.isDeviceMuted = true
    } else {
      this.isDeviceMuted = false
    }
    if (this.g711Processor) {
      this.g711Processor.port.postMessage({ type: 'mute', value: isMuted })
    }
  }

  adjustVolume(volume) {
    this.g711Processor.port.postMessage({ type: 'gain', value: volume })
  }

  addEventListenerWebSocket(
    callbackCallActiveAfterBuffering,
    callbakDeviceBuffering,
    callbackCallsOnGoing,
    callbackCallsFailed,
    callbackWsError
  ) {
    return {
      onopen: () => {
        // eslint-disable-next-line no-console
        console.log('WebSocket voip connection opened')
      },
      onmessage: (event) => {
        const data = event.data
        if (data instanceof Blob) {
          if (!this.isMessageHaveBeenReceived) {
            this.isMessageHaveBeenReceived = true
            this.startMessageCheck(callbakDeviceBuffering)
          }
          if (this.isDeviceBuffering) {
            callbackCallActiveAfterBuffering()
            this.isDeviceBuffering = false
          }
          this.lastMessageTimestamp = Date.now()
          callbackCallsOnGoing()
          if (!this.isDeviceMuted) {
            playG711Audio(data, this.socket.workletNode)
          }
        } else {
          this.stopCapture()
          this.stopMessageCheck()
        }
      },
      onclose: (event) => {
        // eslint-disable-next-line no-console
        console.log('WebSocket voip connection closed:', event.code, event.reason)
        callbackCallsFailed(event.reason)
        this.stopMessageCheck()
        this.isMessageHaveBeenReceived = false
      },
      onerror: (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket voip error:', error)
        callbackWsError()
        this.stopMessageCheck()
        this.isMessageHaveBeenReceived = false
      },
    }
  }

  startMessageCheck(callbakDeviceBuffering) {
    this.checkMessageInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTimestamp

      if (timeSinceLastMessage > this.messageTimeout) {
        // No message received for more than 3 seconds
        this.isDeviceBuffering = true
        callbakDeviceBuffering()
      }
    }, 1000)
  }

  stopMessageCheck() {
    if (this.checkMessageInterval) {
      clearInterval(this.checkMessageInterval)
    }
  }
}
